/**
 * Phone number validation utilities
 * Ensures all phone numbers include country codes for WhatsApp compatibility
 */

export interface PhoneValidationResult {
  isValid: boolean
  message?: string
  formattedNumber?: string
}

/**
 * Common country codes for the region
 */
export const COMMON_COUNTRY_CODES = [
  { code: '+856', country: 'Laos', flag: '🇱🇦' },
  { code: '+66', country: 'Thailand', flag: '🇹🇭' },
  { code: '+84', country: 'Vietnam', flag: '🇻🇳' },
  { code: '+86', country: 'China', flag: '🇨🇳' },
  { code: '+33', country: 'France', flag: '🇫🇷' },
  { code: '+44', country: 'UK', flag: '🇬🇧' },
  { code: '+1', country: 'USA/Canada', flag: '🇺🇸' },
  { code: '+49', country: 'Germany', flag: '🇩🇪' },
  { code: '+61', country: 'Australia', flag: '🇦🇺' },
  { code: '+91', country: 'India', flag: '🇮🇳' },
  { code: '+81', country: 'Japan', flag: '🇯🇵' },
  { code: '+82', country: 'South Korea', flag: '🇰🇷' },
]

/**
 * Validates a phone number and checks for country code
 * @param phone - Phone number string to validate
 * @returns Validation result with helpful message
 */
export function validatePhoneNumber(phone: string): PhoneValidationResult {
  if (!phone || phone.trim() === '') {
    return {
      isValid: false,
      message: 'Please enter your phone number'
    }
  }

  const cleaned = phone.trim()

  // Detect "00" prefix (common mistake: 0033 instead of +33, 0066 instead of +66)
  if (cleaned.startsWith('00') && !cleaned.startsWith('+')) {
    const corrected = '+' + cleaned.slice(2)
    return {
      isValid: false,
      message: `Use + instead of 00. Try: ${corrected}`
    }
  }

  // Detect local format starting with 0 (e.g., 020 xxx for Laos, 06 xxx for France)
  if (/^0\d/.test(cleaned) && !cleaned.startsWith('+')) {
    return {
      isValid: false,
      message: 'Please use international format with + and your country code (e.g., +33 for France, +856 for Laos, +66 for Thailand)'
    }
  }

  // Check if it starts with a plus sign (international format)
  if (!cleaned.startsWith('+')) {
    return {
      isValid: false,
      message: 'Please start with + and your country code (e.g., +33 for France, +856 for Laos, +66 for Thailand)'
    }
  }

  // Basic validation: + followed by 1-3 digit country code, then 6-15 digits
  const digitsOnly = cleaned.replace(/[\s\-()]/g, '')
  const phoneRegex = /^\+\d{1,3}\d{6,15}$/

  if (!phoneRegex.test(digitsOnly)) {
    return {
      isValid: false,
      message: 'Please enter a valid phone number with country code (e.g., +33 6 12 34 56 78)'
    }
  }

  // Format the number by removing extra spaces/dashes but keeping the structure readable
  const formattedNumber = cleaned.replace(/[\s\-()]+/g, ' ').trim()

  return {
    isValid: true,
    formattedNumber,
    message: undefined
  }
}

/**
 * Gets a friendly example based on detected country code
 * @param phone - Partial phone number
 * @returns Example phone number
 */
export function getPhoneExample(phone: string): string {
  const cleaned = phone.trim()

  // Try to detect country from partial input
  for (const { code } of COMMON_COUNTRY_CODES) {
    if (cleaned.startsWith(code)) {
      switch (code) {
        case '+856': return '+856 20 1234 5678'
        case '+66': return '+66 81 234 5678'
        case '+84': return '+84 90 123 4567'
        case '+33': return '+33 6 12 34 56 78'
        case '+44': return '+44 7700 900123'
        case '+1': return '+1 555 123 4567'
        default: return `${code} xxx xxx xxx`
      }
    }
  }

  return '+856 20 1234 5678' // Default to Laos format
}

/**
 * Format a phone number for display (removes excessive spaces)
 * @param phone - Phone number to format
 * @returns Formatted phone number
 */
export function formatPhoneForDisplay(phone: string): string {
  if (!phone) return ''
  return phone.replace(/[\s\-()]+/g, ' ').trim()
}

/**
 * Format a phone number for API/database (international format, no spaces)
 * @param phone - Phone number to format
 * @returns API-ready phone number
 */
export function formatPhoneForAPI(phone: string): string {
  if (!phone) return ''
  return phone.replace(/[\s\-()]+/g, '').trim()
}
